import { Anchor, Bug, CarFront, Gem, HeartPulse, HardHat, Plane, ShieldCheck } from "lucide-react";

export const coverage = [
  {
    id: "marine", icon: Anchor, label: "Marine", vibe: "for the things in transit",
    title: "Cargo should arrive. Your cover should too.",
    copy: "Protect goods on the move against breakage, theft, weather and the unexpected detour.",
    tags: ["Cargo", "Transit", "War risk"], color: "coral",
    includes: [
      "Loss or damage to goods in transit by sea, road, rail or air",
      "Theft, pilferage and non-delivery of consignments",
      "Weather, collision, overturning and handling damage",
      "War, strike and civil commotion risks (as add-ons)",
      "Warehouse-to-warehouse continuity for the full journey",
    ],
    watch: [
      "Packaging clauses — poor packing can void an otherwise valid claim",
      "Per-shipment vs annual open cover — the wrong structure leaves silent gaps",
      "Pure delay losses are usually excluded unless specifically added",
      "Sub-limits on fragile, refrigerated or high-value cargo",
    ],
    faqs: [
      { q: "My transporter is insured. Isn’t that enough?", a: "No — their policy covers their legal liability, not the full value of your goods. Your own marine cover pays you first and argues later." },
      { q: "Is a single shipment worth insuring?", a: "Yes. Single-transit policies are inexpensive and take minutes to place. If the shipment’s value would hurt, insure it." },
      { q: "Do exports need different cover?", a: "Incoterms decide who insures which leg of the journey. We map that before you ship so neither side assumes the other covered it." },
    ],
  },
  {
    id: "travel", icon: Plane, label: "Travel", vibe: "for plans that go places",
    title: "Go explore. Leave the what-ifs here.",
    copy: "Stay ready for medical emergencies, trip interruptions, lost baggage and the little plot twists far from home.",
    tags: ["Medical", "Baggage", "Delay"], color: "sky",
    includes: [
      "Emergency medical treatment and evacuation abroad",
      "Trip cancellation, curtailment and interruption costs",
      "Lost, delayed or damaged baggage and passport loss",
      "Personal liability and flight delay benefits",
      "Missed connections and emergency hotel stays",
    ],
    watch: [
      "Pre-existing conditions are excluded unless declared and accepted",
      "Adventure sports usually need a specific add-on",
      "Claim intimation windows are tight — often 24 to 48 hours",
      "Visa rejection cover is a separate benefit, not standard",
    ],
    faqs: [
      { q: "Should I just buy the airline’s add-on at checkout?", a: "Usually no — checkout plans are often overpriced with thin medical limits. A standalone plan typically does more for less." },
      { q: "Does it cover parents above 60?", a: "Yes, through senior-specific plans. Full medical disclosure matters more than the premium you save by skipping it." },
      { q: "Are pandemic-type disruptions covered?", a: "Most current plans include them, but the epidemic clause varies widely. This is exactly the fine print we read for you." },
    ],
  },
  {
    id: "cyber", icon: Bug, label: "Cyber", vibe: "for the internet plot twists",
    title: "One click shouldn’t become a crisis.",
    copy: "Build a response plan for data leaks, ransomware, business interruption and digital extortion.",
    tags: ["Data", "Ransomware", "Downtime"], color: "lime",
    includes: [
      "Data breach response, forensics and customer notification",
      "Ransomware, extortion payments and negotiation support",
      "Business interruption from system outages",
      "Third-party liability, regulatory defence and penalties",
      "PR and reputation management after an incident",
    ],
    watch: [
      "Funds-transfer fraud and social engineering often need a separate extension",
      "No multi-factor authentication can void the policy entirely",
      "Retroactive dates decide whether an old, undiscovered breach is covered",
      "Cloud and vendor outages may sit outside standard wording",
    ],
    faqs: [
      { q: "We’re small. Do hackers really care?", a: "Yes — small firms are the most-attacked segment precisely because defences are thin and ransoms get paid quickly." },
      { q: "Isn’t our IT vendor responsible for breaches?", a: "Contracts rarely transfer real liability to vendors. When customer data leaks, the regulator and the customers come to you." },
      { q: "What does meaningful cover cost?", a: "For most small businesses, less than a single hour of downtime. The audit tells you exactly where your exposure sits." },
    ],
  },
  {
    id: "liability", icon: ShieldCheck, label: "Liability", vibe: "for when ‘my bad’ is expensive",
    title: "Keep the business moving when claims show up.",
    copy: "Cover the legal and financial impact of third-party injury, damage or professional mistakes.",
    tags: ["CGL", "Errors", "Defence"], color: "blue",
    includes: [
      "Third-party bodily injury and property damage (CGL)",
      "Legal defence costs, even for frivolous claims",
      "Product liability for goods you make or sell",
      "Professional indemnity for errors, omissions and bad advice",
      "Directors & officers protection for leadership decisions",
    ],
    watch: [
      "Claims-made vs occurrence wording changes what’s actually covered",
      "Liability you accept in contracts is usually excluded",
      "A broken retroactive date kills claims from past work",
      "Territory and jurisdiction limits matter if you export",
    ],
    faqs: [
      { q: "We’ve never been sued. Why bother?", a: "Liability claims arrive without warning and defence costs alone can run into lakhs before fault is even decided." },
      { q: "Does CGL cover professional mistakes?", a: "No — that needs professional indemnity. Many businesses carry one and wrongly assume it does the other’s job." },
      { q: "How much limit is enough?", a: "It depends on your contracts, clients and exposure. The risk audit sizes this from evidence, not guesswork." },
    ],
  },
  {
    id: "jewellers", icon: Gem, label: "Jewellers", vibe: "for sparkle with a safety net",
    title: "High-value stock needs high-attention cover.",
    copy: "Specialist protection for jewellery in-store, in transit and in the hands of your team.",
    tags: ["Stock", "Theft", "Transit"], color: "pink",
    includes: [
      "Stock at premises, including safe and vault storage",
      "Stock in transit between stores, exhibitions and clients",
      "Employee infidelity and insider theft",
      "Robbery, burglary and hold-up cover",
      "Window display and exhibition showcase protection",
    ],
    watch: [
      "Stock declaration discipline is a policy condition, not a formality",
      "CCTV, safe and alarm specifications are underwriting requirements",
      "Exhibitions often need separate intimation or extension",
      "Valuation basis — cost vs retail — changes what a claim pays",
    ],
    faqs: [
      { q: "Is stock covered while a customer tries it on?", a: "Usually yes within the premises, but the moment it leaves the shop the transit clause takes over — with its own conditions." },
      { q: "We do exhibitions often. Are we covered?", a: "Only if exhibitions are declared and endorsed. Unattended exhibition stock is one of the most common declined claims." },
      { q: "Does the policy cover smithing and repair work?", a: "Customer goods in your custody can be added. It’s a specific extension most jewellers discover only after a loss." },
    ],
  },
  {
    id: "fleet", icon: CarFront, label: "Fleet", vibe: "for the whole squad on wheels",
    title: "Every vehicle. One less thing to chase.",
    copy: "Bring your commercial vehicles, drivers and everyday road risks into one clear strategy.",
    tags: ["Vehicles", "Drivers", "Damage"], color: "yellow",
    includes: [
      "Own-damage cover for every commercial vehicle",
      "Third-party liability as required by law",
      "Personal accident cover for drivers and cleaners",
      "Zero-dep, roadside assistance and engine protection add-ons",
      "Coordination with marine cover for goods being carried",
    ],
    watch: [
      "An incorrect IDV quietly shrinks every total-loss payout",
      "Goods vs passenger usage must match the policy class",
      "Driving-licence validity clauses are enforced strictly",
      "Renewal lapses reset no-claim bonuses across the fleet",
    ],
    faqs: [
      { q: "Is one fleet policy better than many single ones?", a: "For fleets of five or more, usually yes — one renewal date, negotiated rates and far less admin." },
      { q: "Are goods in the truck covered by motor insurance?", a: "No — cargo needs marine/transit cover. We pair the two so neither the vehicle nor the load is exposed." },
      { q: "What if a driver has an accident off duty?", a: "That depends on usage terms. Personal-use allowances must be written into the policy, not assumed." },
    ],
  },
  {
    id: "personal", icon: HeartPulse, label: "Personal", vibe: "for the life behind the work",
    title: "Protect your people, not just your P&L.",
    copy: "Health, term life and personal accident cover for you, your spouse and your children.",
    tags: ["Health", "Life", "Family"], color: "mint",
    includes: [
      "Health insurance — family floater or individual structures",
      "Term life cover sized to real liabilities and income",
      "Personal accident and disability income protection",
      "Critical illness cover for the big diagnoses",
      "Home contents and structure protection",
    ],
    watch: [
      "Waiting periods on pre-existing conditions before claims pay",
      "Room-rent sub-limits that silently shrink hospital claims",
      "Employer cover ends the day the job does",
      "Under-declared income can limit term cover eligibility",
    ],
    faqs: [
      { q: "Is my company’s group health plan enough?", a: "It’s a start, but it disappears with the job and rarely fits your family’s needs. Personal cover stays with you." },
      { q: "How much term cover do I actually need?", a: "A common anchor is 10–15× annual income plus outstanding loans. We size it from your real numbers." },
      { q: "Should parents join my floater?", a: "Usually no — senior parents are better on their own plan so one large claim doesn’t exhaust the family’s cover." },
    ],
  },
  {
    id: "construction", icon: HardHat, label: "Construction", vibe: "for building big things",
    title: "Big builds come with big what-ifs.",
    copy: "Cover projects, equipment, workers and third-party exposure from ground-up to handover.",
    tags: ["Projects", "Plant", "Workers"], color: "orange",
    includes: [
      "Contractor’s All Risk (CAR) for the project itself",
      "Contractor’s plant, machinery and equipment",
      "Workmen compensation for site labour",
      "Third-party liability for site-adjacent damage",
      "Delay in start-up (DSU) cover for larger projects",
    ],
    watch: [
      "Maintenance-period cover is frequently forgotten at handover",
      "Sub-contractor work must be expressly included",
      "Sum insured must reflect full project value, not contract value tricks",
      "Defects-liability exclusions are common and often misunderstood",
    ],
    faqs: [
      { q: "Who should buy CAR — owner or contractor?", a: "Either can, but the contract should say clearly. Double insurance and no insurance are equally expensive mistakes." },
      { q: "Are labourers without formal contracts covered?", a: "Workmen compensation can cover them, but payroll and headcount records decide whether a claim succeeds." },
      { q: "Does CAR cover faulty design?", a: "Standard wording excludes pure design defects. There are LEG/DE extensions — this is exactly where advice pays for itself." },
    ],
  },
];
